import * as moment from "moment";
import { CaseType } from "@teamprevent/common/model";
import { Payload } from "@neworbit/redux-helpers";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import * as immunisationReviewActions from "@teamprevent/common/questionnaires/components/immunisationReview/actionTypes";

import { ApplicationState } from "../../applicationState";

import * as clinicalNotesActions from "../../clinicalNotes/actionTypes";
import * as bloodTestActions from "../../bloodTest/actionTypes";
import * as vaccinationActions from "../../vaccinationConsultation/actionTypes";
import * as bbvActions from "../../bbvConsultation/actionTypes";
import * as referralReportActions from "../../referralReport/actionTypes";
import * as bookAppointmentsActions from "../../bookAppointments/actionTypes";
import * as bbvReportActions from "../../bbvReport/actionTypes";
import * as immunisationOhCaseActions from "../../immunisationOhCase/actionTypes";
import * as videoMeetingsActions from "../../videoMeeting/actionTypes";
import * as mhpTreatmentActions from "../../mentalHealthPractitionerTreatment/actionTypes";
import * as ppqActions from "../../ppqReport/actionTypes";

import * as actions from "./actiontypes";
import { ReducerAction } from "./reducer";
import { CaseStatus } from "./model";

export function ohCase(state: ApplicationState["ohCases"][number], action: ReducerAction): ApplicationState["ohCases"][number] {
    switch (action.type) {
        case actions.UPDATE_ASSOCIATED_MANAGERS_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                return { listUpdated: moment(), ...state, associatedManagers: action.payload.associatedManagers, detailUpdated: moment() };
            }
            return state;
        case actions.UPDATE_ASSOCIATED_HR_USERS_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                return { listUpdated: moment(), ...state, associatedHrUsers: action.payload.associatedHrUsers, detailUpdated: moment() };
            }
            return state;
        case actions.UPDATE_PURCHASE_ORDER_NUMBER_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                return { listUpdated: moment(), ...state, purchaseOrderNumber: action.payload.purchaseOrderNumber, detailUpdated: moment() };
            }
            return state;
        case actions.SET_OHCASE_CONFIDENTIALITY_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                const tasks = updateTasks(state, action);
                return {
                    listUpdated: moment(),
                    ...state,
                    totallyConfidential: action.payload.totallyConfidential,
                    detailUpdated: moment(),
                    tasks
                };
            }
            return state;
        case actions.LOAD_OHCASE_DETAIL_SUCCESS:
            if (action.payload.id === state.id) {
                return detailUpdated(action.payload, state);
            }
            return state;
        case clinicalNotesActions.SUBMIT_CLINICAL_NOTES_SUCCESS:
        case clinicalNotesActions.SUBMIT_DID_NOT_ATTEND_SUCCESS:
        case bloodTestActions.SUBMIT_BLOOD_TEST_CONSULTATION_SUCCESS:
        case vaccinationActions.SUBMIT_VACCINATION_CONSULTATION_SUCCESS:
        case bbvActions.SUBMIT_BBV_CONSULTATION_SUCCESS:
        case bbvReportActions.SUBMIT_BBV_REPORT_SUCCESS:
        case bookAppointmentsActions.BOOK_AD_HOC_APPOINTMENT_SUCCESS:
        case bookAppointmentsActions.BOOK_INTO_CLINIC_SUCCESS:
        case bookAppointmentsActions.REBOOK_APPOINTMENT_SUCCESS:
        case actions.ACTION_TASK_SUCCESS:
        case actions.EDIT_TASK_COMPLETION_DATA_SUCCESS:
        case videoMeetingsActions.CREATE_TEAMS_MEETING_SUCCESS:
        case videoMeetingsActions.UNABLE_TO_BOOK_VIDEO_APPOINTMENT_SUCCESS:
        case videoMeetingsActions.COMPLETE_CREATE_VIDEO_CONSULTATION_SUCCESS:
        case referralReportActions.REQUEST_CHANGES_REFERRAL_REPORT_SUCCESS:
        case referralReportActions.REQUEST_REFERRAL_REPORT_EDIT_SUCCESS:
        case referralReportActions.REJECT_REFERRAL_REPORT_SUCCESS:
        case ppqActions.REQUEST_CHANGES_PPQ_CERTIFICATE_SUCCESS:
        case ppqActions.REJECT_PPQ_CERTIFICATE_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                const tasks = updateTasks(state, action);
                const caseStatus = getNewOhCaseStatus(state, tasks);
                return {
                    ...state,
                    tasks,
                    caseStatus,
                    tasksCompletedInCurrentSession: true
                };
            }
            return state;
        case actions.DELETE_TASK_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                const tasks = updateWithDeletedTasks(state, action);
                const caseStatus = getNewOhCaseStatus(state, tasks);
                return {
                    ...state,
                    tasks,
                    caseStatus,
                    tasksCompletedInCurrentSession: true
                };
            }
            return state;
        case referralReportActions.SUBMIT_REFERRAL_REPORT_SUCCESS:
        case referralReportActions.SENDTOEMPLOYEE_REFERRAL_REPORT_SUCCESS:
        case referralReportActions.APPROVE_REFERRAL_REPORT_SUCCESS:
        case mhpTreatmentActions.SUBMIT_MHP_TREATMENT_REPORT_SUCCESS:
        case mhpTreatmentActions.APPROVE_MHP_TREATMENT_REPORT_SUCCESS:
        case ppqActions.APPROVE_PPQ_CERTIFICATE_SUCCESS:
        case ppqActions.SENDTOEMPLOYEE_PPQ_CERTIFICATE_SUCCESS:
            if (action.payload.id === state.id) {
                const tasks = [...action.payload.tasks];
                const linkedOhCases = [...action.payload.linkedOhCases];
                const newState = {
                    ...state,
                    tasks,
                    linkedOhCases
                };
                return detailUpdated(newState, state);
            }
            return state;
        case immunisationReviewActions.SUBMIT_IMMUNISATION_REVIEW_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                const tasks = updateTasks(state, action);
                const caseStatus = getNewOhCaseStatus(state, tasks);
                const linkedOhCases = [...(action.payload.linkedOhCases ?? [])];
                return {
                    ...state,
                    tasks,
                    caseStatus,
                    linkedOhCases,
                    immunisations: action.payload.immunisations,
                    tasksCompletedInCurrentSession: true
                };
            }
            return state;
        case actions.CREATE_OHCASE_DOCUMENT_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                return { ...state , documents: [...(state.documents || []), action.payload.document] };
            }
            return state;
        case actions.UPDATE_OHCASE_DOCUMENT_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                const documents = state.documents?.map(d => d.id === action.payload.document.id ? action.payload.document : d);
                return { ...state , documents };
            }
            return state;
        case actions.LOAD_OHCASE_DOCUMENTS_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                return { ...state, documents: action.payload.documents };
            }
            return state;
        case actions.DELETE_OHCASE_DOCUMENT_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                return { ...state, documents: state.documents?.filter(d => d.id !== action.payload.ohCaseDocumentId) };
            }
            return state;
        case actions.CREATE_TASK_SUCCESS:
            if (action.payload.caseId === state.id) {
                const tasks = [...state.tasks, action.payload];
                return { ...state, tasks, caseStatus: getNewOhCaseStatus(state, tasks) };
            }
            return state;
        case actions.CREATE_TASKS_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                const tasks = updateTasks(state, action);
                const caseStatus = getNewOhCaseStatus(state, tasks);
                return {
                    ...state,
                    tasks,
                    caseStatus,
                };
            }
            return state;
        case actions.CANCEL_TASK_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                const tasks = [...state.tasks.filter(task => task.id !== action.payload.taskId), ...action.payload.tasks];
                return {
                    ...state,
                    tasks,
                    caseStatus: getNewOhCaseStatus(state, tasks)
                };
            }
            return state;
        case immunisationOhCaseActions.MARK_OH_CASE_IMMUNISATION_CLEAR_SUCCESS:
            if (action.payload.ohCaseId === state.id) {
                return { ...state, linkedOhCases: state.linkedOhCases.map(l => ({ ...l, immsClear: true })) };
            }
            if (state.linkedOhCases && state.linkedOhCases.some(l => l.id === action.payload.ohCaseId)) {
                return { ...state, linkedOhCases: state.linkedOhCases.map(l => l.id === action.payload.ohCaseId ? { ...l, immsClear: true } : l) };
            }
            return state;
        case actions.CREATE_IMMUNISATION_CASE_SUCCESS:
            if (action.payload.linkedOhCases && action.payload.linkedOhCases.some(linkedCase => linkedCase.id === state.id)) {
                return { ...state, linkedOhCases: [...state.linkedOhCases, { id: action.payload.id, caseType: CaseType.IMMUNISATION }] };
            }
            return state;
        default:
            return state;
    }
};

function updateTasks(state: { tasks?: TaskModel[] }, action: Payload<{ tasks: TaskModel[] }>) {
    const tasks = [...state.tasks];
    action.payload.tasks.forEach(t => {
        const taskIndex = tasks.findIndex(x => x.id === t.id);
        if (taskIndex > -1) {
            tasks[taskIndex] = t;
        } else {
            tasks.push(t);
        }
    });
    return tasks;
}

function updateWithDeletedTasks(state: { tasks?: TaskModel[] }, action: Payload<{ tasks: TaskModel[] }>) {
    const tasks = [...state.tasks];
    action.payload.tasks.forEach(t => {
        const taskIndex = tasks.findIndex(x => x.id === t.id);
        if (taskIndex > -1) {
            tasks[taskIndex] = t;
        } else {
            tasks.push(t);
        }
    });
    const newTasks = tasks.filter(t => {
        const taskIndex = action.payload.tasks.findIndex(x => x.id === t.id);
        return taskIndex > -1;
    });
    return newTasks;
}

const getNewOhCaseStatus = (state: { caseStatus?: CaseStatus }, tasks: TaskModel[]) => {
    if (state.caseStatus !== "Open") {
        return state.caseStatus;
    }

    if (tasks.every(t => t.completed || t.doesNotAffectCaseStatus)) {
        return "Closed";
    }

    return state.caseStatus;
};

export function detailUpdated(
    value: ApplicationState["ohCases"][number],
    state: ApplicationState["ohCases"][number] = {} as ApplicationState["ohCases"][number]): ApplicationState["ohCases"][number]
{
    const valueDocuments = value.documents;
    const stateDocuments = state.documents;

    if (!valueDocuments || valueDocuments.length === 0)
    {
        return { listUpdated: moment(), ...state, ...value, documents: stateDocuments, detailUpdated: moment() };
    }

    return { listUpdated: moment(), ...state, ...value, detailUpdated: moment() };
}
